import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Mic, Loader2, AlertTriangle, CreditCard } from "lucide-react";

const dollars = (cents) => `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`;

function detailMsg(e, fallback) {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object") return d.message || fallback;
  return d || fallback;
}

/**
 * Instant interview-credit top-up. Opens a pack picker, charges the card on file
 * (POST /company/addons/interviews/charge), grants credits, and refreshes auth.
 * Credits are only granted when the payment succeeds (backend enforces this).
 *
 * Props:
 *   className   - trigger button styling
 *   label       - trigger button text (default "Top up interviews")
 *   recommend   - optional shortfall N: highlights the smallest pack that covers it
 *   onPurchased - called after a successful purchase
 */
export default function TopUpButton({ className, label, recommend, onPurchased }) {
  const { user, checkAuth } = useAuth();
  const billing = user?.company_billing || {};
  const packs = billing.interview_packs || [10, 25, 50];
  const priceCents = billing.topup_price_cents ?? 150;

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Smallest pack that covers the shortfall (for a subtle "recommended" hint).
  const recommended = recommend ? packs.find((p) => p >= recommend) ?? packs[packs.length - 1] : null;

  const buy = async (size) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/company/addons/interviews/charge", { pack_size: size });
      await checkAuth();
      toast.success(`${size} interview credits added — ${dollars(data.charged_cents)} charged`);
      setOpen(false);
      onPurchased?.();
    } catch (e) {
      setError(detailMsg(e, "Could not complete purchase"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => { setError(null); setOpen(true); }} disabled={busy} className={className}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mic className="w-3.5 h-3.5" />}
        {label ?? "Top up interviews"}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={() => !busy && setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Mic className="w-4 h-4 text-[#0025D8]" />
              <h3 className="font-display font-bold text-lg">Buy interview credits</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Charged to your card on file. Credits never expire and carry over each cycle.
              {recommend ? ` You need at least ${recommend} more to send all invites.` : ""}
            </p>

            <div className="space-y-2 mb-4">
              {packs.map((size) => (
                <button key={size} onClick={() => buy(size)} disabled={busy}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-colors disabled:opacity-60 ${
                    size === recommended
                      ? "border-[#0025D8] bg-[#0025D8]/5 hover:bg-[#0025D8]/10"
                      : "border-border hover:bg-secondary"
                  }`}>
                  <span className="flex items-center gap-2">
                    <span className="font-semibold text-sm">{size} interviews</span>
                    {size === recommended && (
                      <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#0025D8] text-white">Recommended</span>
                    )}
                  </span>
                  <span className="font-mono text-sm">{dollars(size * priceCents)}</span>
                </button>
              ))}
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-lg bg-[#E11D48]/10 border border-[#E11D48]/30 px-3 py-2.5 mb-4">
                <AlertTriangle className="w-3.5 h-3.5 text-[#E11D48] flex-shrink-0 mt-0.5" />
                <div className="text-xs text-[#E11D48]">
                  {error}{" "}
                  <Link to="/company/billing" className="underline font-semibold" onClick={() => setOpen(false)}>
                    Update card
                  </Link>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                <CreditCard className="w-3 h-3" /> {dollars(priceCents)} per interview
              </span>
              <button onClick={() => setOpen(false)} disabled={busy}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-border text-muted-foreground hover:bg-secondary transition-colors disabled:opacity-60">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
